export type PrevalenceDataInput = {
  disease_prevalence_class?: string | null;
  disease_prevalence_geo_area?: string | null;
  disease_prevalence_type?: string | null;
};

export type PrevalenceEntry = {
  class: string;
  type: string;
  geography: string;
};

export type PrevalenceVisualData = {
  barPercent: number;
  casesPer100k: number;
  percentPopulation: number;
};

const COMPACT_BASE_REGEX = /\s*\/\s*100 000\b/g;
const HYPHEN_BETWEEN_NUMBERS_REGEX = /(\d)-(\d)/g;

function splitCommaList(value?: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
}

function compactPrevalenceClass(value: string): string {
  return value.replace(COMPACT_BASE_REGEX, ' / 100k').replace(HYPHEN_BETWEEN_NUMBERS_REGEX, '$1-$2');
}

export function parsePrevalenceData({
  disease_prevalence_class,
  disease_prevalence_geo_area,
  disease_prevalence_type,
}: PrevalenceDataInput): PrevalenceEntry[] {
  const classes = splitCommaList(disease_prevalence_class);
  const geographies = splitCommaList(disease_prevalence_geo_area);
  const types = splitCommaList(disease_prevalence_type);

  const maxLen = Math.max(classes.length, geographies.length, types.length);
  if (maxLen === 0) return [];

  const entries: PrevalenceEntry[] = [];
  for (let i = 0; i < maxLen; i += 1) {
    const entry: PrevalenceEntry = {
      class: classes[i] ?? '',
      type: types[i] ?? '',
      geography: geographies[i] ?? '',
    };

    if (entry.class || entry.type || entry.geography) {
      entries.push(entry);
    }
  }

  return entries;
}

export function getPrevalenceSummary(entries: PrevalenceEntry[]): string {
  if (!entries.length) return '-';
  const firstClass = compactPrevalenceClass(entries[0].class || 'Unknown');
  const regionsLabel = entries.length === 1 ? 'region' : 'regions';
  return `${firstClass} · ${entries.length} ${regionsLabel}`;
}

export function formatPrevalenceClass(value: string): string {
  if (!value) return '-';
  return compactPrevalenceClass(value);
}

function parseNumericToken(token: string): number | null {
  const parsed = Number(token.replace(/\s/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function estimateNumerator(numeratorText: string): number | null {
  const clean = numeratorText.trim();
  if (!clean) return null;

  const rangeMatch = clean.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    const min = parseNumericToken(rangeMatch[1]);
    const max = parseNumericToken(rangeMatch[2]);
    if (min == null || max == null) return null;
    return (min + max) / 2;
  }

  const gtMatch = clean.match(/>\s*(\d+(?:\.\d+)?)/);
  if (gtMatch) {
    const base = parseNumericToken(gtMatch[1]);
    return base == null ? null : base * 1.25;
  }

  const ltMatch = clean.match(/<\s*(\d+(?:\.\d+)?)/);
  if (ltMatch) {
    const base = parseNumericToken(ltMatch[1]);
    return base == null ? null : base * 0.5;
  }

  return parseNumericToken(clean);
}

export function getPrevalenceVisualData(value: string): PrevalenceVisualData | null {
  if (!value) return null;
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (/unknown|not yet documented/i.test(normalized)) return null;

  const parts = normalized.split('/');
  if (parts.length !== 2) return null;

  const numerator = estimateNumerator(parts[0]);
  const denominator = parseNumericToken(parts[1]);
  if (numerator == null || denominator == null || denominator <= 0) return null;

  const ratio = numerator / denominator;
  const casesPer100k = ratio * 100000;
  const percentPopulation = ratio * 100;
  const maxCasesPer100k = 100;
  const logDen = Math.log10(maxCasesPer100k + 1);
  const barPercent = Math.max(0, Math.min(100, (Math.log10(casesPer100k + 1) / logDen) * 100));

  return {
    barPercent,
    casesPer100k,
    percentPopulation,
  };
}
